import React, { useEffect, useRef, useState } from "react";
import { RISK_STATUSES, resolveRisk } from "../../lib/risk";

/**
 * KanbanRiskPill — compact clickable RAG badge for a Kanban card.
 * Renders a coloured dot + short label. Clicking opens a small inline
 * popover with Red / Amber / Green choices; picking one fires
 * onChange(nextKey) and closes the popover.
 *
 * The parent is responsible for optimistically updating local state
 * AND persisting via PATCH /projects/{pid} { risk_status: nextKey }.
 */
export default function KanbanRiskPill({ projectId, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const cur = resolveRisk(value);
  const curMeta = RISK_STATUSES.find(r => r.key === cur) || RISK_STATUSES[2];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const pick = (key) => {
    setOpen(false);
    if (key !== cur) onChange?.(key);
  };

  return (
    <span ref={wrapRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => {
          // Prevent the drag-start that lives on the card wrapper.
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setOpen(o => !o);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
        draggable={false}
        disabled={disabled}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0"
        style={{
          background: `${curMeta.tone}18`,
          color: curMeta.tone,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        title={disabled
          ? `Risk: ${curMeta.label}`
          : `Risk: ${curMeta.label} — click to change`}
        aria-label={`Risk rating: ${curMeta.label}. Click to change.`}
        data-testid={`pm-card-risk-${projectId}`}
        data-risk-value={cur}
      >
        <span
          aria-hidden
          style={{
            width: 7, height: 7, borderRadius: "50%",
            background: curMeta.tone, display: "inline-block",
          }}
        />
        {curMeta.label.toUpperCase()}
      </button>
      {open && (
        <div
          className="absolute z-20 top-full left-0 mt-1 bg-white border border-[var(--line)] rounded shadow-lg py-1 min-w-[100px]"
          data-testid={`pm-card-risk-menu-${projectId}`}
        >
          {RISK_STATUSES.map(r => (
            <button
              key={r.key}
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); pick(r.key); }}
              className={`w-full text-left px-2.5 py-1 text-[10px] flex items-center gap-1.5 hover:bg-[var(--surface-2,#F6F7F9)] ${cur === r.key ? "font-bold" : ""}`}
              data-testid={`pm-card-risk-option-${projectId}-${r.key}`}
            >
              <span
                aria-hidden
                style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: r.tone, display: "inline-block",
                }}
              />
              <span style={{ color: r.tone }}>{r.label}</span>
              {cur === r.key && <span className="muted ml-auto">✓</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
